// Client for the editor content endpoints (CONTRACTS §2 — POST /draft, /commit).
// Content writes are server-side only (firestore.rules denies client writes to
// docs/* and versions/*), so inline editing calls these Cloud Functions. The
// __session cookie rides along automatically on the same-origin request.

/** Raised when the local base version is behind the server's currentVersion. */
export class StaleVersionError extends Error {
  constructor(public currentVersion?: string) {
    super("This doc has a newer version. Reload to continue editing.");
    this.name = "StaleVersionError";
  }
}

/** Raised when a commit is blocked by open comments on the current version. */
export class UnresolvedCommentsError extends Error {
  constructor(public unresolvedCount: number) {
    super(
      `Resolve all comments before publishing a new version (${unresolvedCount} open).`
    );
    this.name = "UnresolvedCommentsError";
  }
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string };
    if (data?.error) return data.error;
  } catch {
    /* keep the fallback */
  }
  return fallback;
}

export interface DraftResult {
  baseVersion: string;
  entry: string;
  bytes: number;
  updatedByEmail: string;
}

/** Persist the edited entry HTML as the shared draft for `docId`. */
export async function saveDraft(
  docId: string,
  baseVersion: string,
  html: string,
  entry?: string
): Promise<DraftResult> {
  const res = await fetch("/draft", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ docId, baseVersion, entry, html }),
    credentials: "same-origin",
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error("Your session expired — sign in again.");
    if (res.status === 403) throw new Error("You do not have edit access to this document.");
    if (res.status === 409) {
      const data = (await res.json().catch(() => null)) as
        | { currentVersion?: string }
        | null;
      throw new StaleVersionError(data?.currentVersion);
    }
    throw new Error(await errorMessage(res, `Saving draft failed (${res.status}).`));
  }
  return ((await res.json()) as { draft: DraftResult }).draft;
}

export interface CommitResult {
  versionId: string;
  contentHash: string;
  fileCount: number;
  bytes: number;
  deduped: boolean;
}

/** Promote the draft into a new immutable version. */
export async function commitVersion(
  docId: string,
  baseVersion: string
): Promise<CommitResult> {
  const res = await fetch("/commit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ docId, baseVersion }),
    credentials: "same-origin",
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error("Your session expired — sign in again.");
    if (res.status === 403) throw new Error("You do not have edit access to this document.");
    if (res.status === 409) {
      const data = (await res.json().catch(() => null)) as
        | { unresolvedCount?: number; currentVersion?: string; error?: string }
        | null;
      if (typeof data?.unresolvedCount === "number") {
        throw new UnresolvedCommentsError(data.unresolvedCount);
      }
      if (data?.currentVersion !== undefined) {
        throw new StaleVersionError(data.currentVersion);
      }
      throw new Error(data?.error ?? "Could not publish a new version.");
    }
    throw new Error(await errorMessage(res, `Publishing failed (${res.status}).`));
  }
  return (await res.json()) as CommitResult;
}
